import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as Yup from 'yup'
import { Formik, Form, Field } from 'formik'
import { TextField } from 'formik-material-ui'
import { KeyboardDatePicker, KeyboardTimePicker } from 'formik-material-ui-pickers'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import dateFormat from 'date-fns/format'
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete'
import Grid from '@material-ui/core/Grid'
import MenuItem from '@material-ui/core/MenuItem'
import LinearProgress from '@material-ui/core/LinearProgress'
import MuiTextField from '@material-ui/core/TextField'

import { Button, Typography } from '../../components/Wrappers'
import { useApiHttpState } from '../../context/ApiContext'
import { useCloseDialog, useOpenDialog } from '../../context/DialogContext'
import { useAddSnackbarQueue } from '../../context/SnackbarContext'
import { useHelperStyles } from '../../styles'
import {
    useAppointment,
    useAppointmentDurations,
    useAppointmentHandlers,
    useAppointmentTypes,
    useBusinessHours,
    useHandlerWeekAppointments,
    useHouseholds,
    useJewishHolidays,
    createAppointment,
    updateAppointment,
} from '../../apis'
import AppointmentCalendar from './AppointmentCalendar'
import CancelConfirmation from './CancelConfirmation'
import useStyles from './styles'
import {
    formatDuration,
    formatHandlerName,
    formatMinutes,
    holidayLabel,
    holidaysByDate,
    isCholHaMoedDay,
    isWorkForbiddenDay,
    toDateOnly,
    toLocalDate,
    toMinutes,
    workForbiddenReason,
} from './calendarHelpers'

/**
 * AppointmentForm - create and edit an appointment (Requirements 1.1, 1.2, 1.4,
 * 1.5, 1.6, 1.7, 13.1) with the selected handler's week beside it
 * (Requirements 9.1, 9.3, 9.6), and cancel one (Requirements 13.2, 13.5).
 *
 * ONE COMPONENT FOR BOTH MODES: pass `appointmentId` (or a preloaded
 * `appointment`) and it edits, pass neither and it creates. Edit mode sends a
 * PARTIAL body containing ONLY the fields that actually changed, which is what
 * the api expects (koa/api/appointments/controller.js update) - a present but
 * blank required field is a validation error there, not a clear.
 *
 * USAGE
 *   //appointments list page (task 11.3), new appointment in the clicked slot
 *   <AppointmentForm householdId={id} date={dateOnly} onSaved={a => {mutate(); close()}} onClose={close} />
 *
 *   //appointments list page / route /app/appointments/:id, editing
 *   <AppointmentForm appointmentId={id} onSaved={() => {mutate(); close()}} onClose={close} />
 *
 * PROPS
 *  appointmentId   number|string  Edit that appointment; the record is fetched
 *                        with useAppointment. Omit for create mode.
 *  appointment     object  An already loaded appointment row (as GET
 *                        /appointments/:id returns it, `notes` and the household
 *                        relation included). Supplying it SKIPS the fetch; it
 *                        also puts the form in edit mode on its own.
 *  householdId, handlerId, date, startTime, durationMinutes
 *                        Create-mode prefills. `date` may be a Date or a
 *                        'YYYY-MM-DD' string, `startTime` a Date, 'HH:MM' or
 *                        'HH:MM:SS'. Ignored in edit mode, where the record wins.
 *  lockHousehold         Keeps the prefilled household selection read-only in
 *                        create mode, for household-scoped creation.
 *  onSaved(appointment, {mode})  Called after the api confirmed a write, with the
 *                        row it returned and mode 'create' | 'edit' | 'cancel'.
 *                        This is where the caller revalidates its own SWR data
 *                        and closes the panel - the form deliberately does not
 *                        navigate or close itself.
 *                        CANCELLING REPORTS THROUGH THE SAME PROP (mode
 *                        'cancel') rather than a second callback, so a caller
 *                        that already wires onSaved cannot end up showing a
 *                        stale list after a cancellation just because it never
 *                        heard about it. Callers that need to tell the cases
 *                        apart can read `mode`.
 *  onClose()       fn    Optional. When given, a "Close" button is rendered.
 *  extraActions    node  Rendered in the action row, LEFT of Cancel/Close/Save.
 *                        A free slot for the caller; the form's own
 *                        Cancel Appointment button does NOT use it (see below).
 *  showHandlerCalendar bool  Defaults true; set false to drop the sidebar (e.g.
 *                        in a narrow dialog).
 *  className       string Appended to the root element's classes.
 *
 * CANCELLING (Requirements 13.2 / 13.5) LIVES HERE, not in the caller, because
 * it is the same two pieces of state the form already holds: whether we are in
 * edit mode and the record's `status`. A caller placing the button through
 * `extraActions` would have to fetch the row a second time just to know whether
 * to render it, and could not freeze the fields of a cancelled appointment
 * anyway. So: the button shows only in edit mode on a still-scheduled record, it
 * opens CancelConfirmation in the shared dialog, and a cancelled record renders
 * frozen with the reason spelled out.
 *
 * NOT ITS JOB: routing, and deciding what to show after a save or a cancellation
 * (the caller's `onSaved`).
 */

//the required set, in the order the fields appear - drives the Requirement 1.5 summary
const REQUIRED_FIELDS = ['householdId', 'handlerId', 'appointmentTypeId', 'date', 'startTime', 'durationMinutes']

// Requirement 13.5 - the one wording for "this record is frozen", used by the
// banner and by the submit guard so the user cannot be told two different things
// about the same state. It matches what the api answers with.
const CANCELLED_MESSAGE = 'This appointment was cancelled. Cancelled appointments cannot be modified.'

const pad2 = value => String(value).padStart(2, '0')

const householdLabel = household => {
    if (!household || typeof household !== 'object') return ''
    const namePart = value => (typeof value === 'string' ? value.trim() : '')
    const familyName = namePart(household.familyName)
    const householdMembers = [namePart(household.husbandName), namePart(household.wifeName)]
        .filter(Boolean)
        .join(' & ')
    const name = [familyName, householdMembers].filter(Boolean).join(', ')
    return name || (household.id === null || household.id === undefined ? '' : `Household #${household.id}`)
}

// 'HH:MM' for the api. A Date comes from the time picker; a stored value is the
// mysql TIME 'HH:MM:SS'. Both are reduced to the same shape so the edit-mode diff
// below compares like with like instead of reporting '09:00' != '09:00:00'.
const toTimeParam = value => {
    if (value instanceof Date) return isNaN(value.getTime()) ? null : dateFormat(value, 'HH:mm')
    const minutes = toMinutes(value)
    if (minutes === null) return null
    return `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`
}

// The picker wants a Date. Built on the appointment's own day (falling back to
// today) from the split parts, so no timezone conversion can move the time.
const toTimeDate = (timeValue, dateOnly) => {
    if (timeValue instanceof Date) return isNaN(timeValue.getTime()) ? null : timeValue
    const minutes = toMinutes(timeValue)
    if (minutes === null) return null
    const base = toLocalDate(dateOnly) || new Date()
    return new Date(base.getFullYear(), base.getMonth(), base.getDate(), Math.floor(minutes / 60), minutes % 60, 0, 0)
}

// Requirement 1.7 / 4.1: the api answers an overlap with 409 and
// `{message, conflict:{date,startTime}}`. The message alone ("Time conflict with
// existing appointment") does not say WHICH slot is taken, so the conflicting
// date and time are spelled out underneath it.
const describeConflict = conflict => {
    if (!conflict) return ''
    const localDate = toLocalDate(conflict.date)
    const day = localDate ? dateFormat(localDate, 'EEEE, MMMM d, yyyy') : ''
    const time = formatMinutes(conflict.startTime)
    if (day && time) return `That handler is already booked on ${day} at ${time}.`
    if (day) return `That handler is already booked on ${day}.`
    if (time) return `That handler is already booked at ${time}.`
    return ''
}

// The api instance resolves to an [error,response] tuple (see ApiContext), so a
// rejected save arrives here as an axios error. Every appointments error body is
// `{status:'failed',message,...}` (koa/middleware/error.js), and the 409 adds
// `conflict` - both are read from `data`, never from er.message, which would show
// the user "Request failed with status code 409".
const readServerError = (er, fallback) => {
    const response = er && er.response ? er.response : null
    const data = response && response.data && typeof response.data === 'object' ? response.data : null
    return {
        status: response ? response.status : null,
        message: (data && data.message) || fallback,
        conflict: data && data.conflict ? data.conflict : null,
    }
}

// Requirements 9.1 / 9.3 / 9.6 - the sidebar. Its own component (rather than
// inline in the Formik render prop) because it needs hooks: the week endpoint is
// keyed on the handler, so SELECTING A DIFFERENT HANDLER CHANGES THE SWR KEY and
// the new handler's week is fetched immediately (Requirement 9.3's 3 seconds is
// one request away, not a poll).
const HandlerWeekCalendar = ({
    handlerId,
    selectedDate,
    selectedStartTime,
    durationMinutes,
    minimumDate,
    reloadToken,
    onSlotSelect,
    excludedAppointmentId,
    slotSelectionDisabled = false,
}) => {
    const classes = useStyles()
    //the week shown follows the date being scheduled, which is the week the user
    //cares about; the calendar's own arrows can then move away from it freely
    const [anchor, setAnchor] = useState(() => selectedDate || toDateOnly(new Date()))
    useEffect(() => {
        if (selectedDate) setAnchor(selectedDate)
    }, [selectedDate])

    const { appointments, isLoading, isError, mutate } = useHandlerWeekAppointments(handlerId, anchor)
    const {
        businessHours,
        isLoading: businessHoursLoading,
        isError: businessHoursError,
    } = useBusinessHours()

    //a rejected save (Requirement 1.7) means somebody else booked that slot, so
    //the week on screen is stale: refetch it with the error rather than leaving a
    //calendar that shows the slot as free
    useEffect(() => {
        if (reloadToken) mutate()
    }, [reloadToken]) // eslint-disable-line react-hooks/exhaustive-deps

    if (!handlerId)
        return (
            <div className={classes.formSidebarMessage}>
                <Typography variant="body2" color="text" colorBrightness="secondary">
                    Select a handler to see the appointments already on their calendar.
                </Typography>
            </div>
        )

    // No callback keeps the calendar read-only. AppointmentForm supplies one
    // while a new or scheduled appointment can change; all other
    // AppointmentCalendar consumers remain unchanged.
    const slotSelection =
        typeof onSlotSelect === 'function'
            ? {
                  durationMinutes,
                  minDate: minimumDate,
                  selectedSlot: { date: selectedDate, startTime: selectedStartTime },
                  excludedAppointmentId,
                  businessHours,
                  businessHoursLoading,
                  businessHoursError,
                  disabled: slotSelectionDisabled,
                  disabledMessage: 'The appointment is being saved. Time slots cannot be changed right now.',
                  onSelect: onSlotSelect,
              }
            : null

    return (
        <AppointmentCalendar
            title="Handler's week"
            appointments={appointments}
            anchorDate={anchor}
            onAnchorDateChange={setAnchor}
            isLoading={isLoading}
            isError={isError}
            onRetry={mutate}
            emptyMessage="This handler has nothing scheduled for this week."
            slotSelection={slotSelection}
        />
    )
}

const AppointmentForm = ({
    appointmentId,
    appointment: appointmentProp,
    householdId: householdIdProp,
    lockHousehold = false,
    handlerId: handlerIdProp,
    date: dateProp,
    startTime: startTimeProp,
    durationMinutes: durationMinutesProp,
    onSaved,
    onClose,
    extraActions,
    showHandlerCalendar = true,
    className,
}) => {
    const classes = useStyles()
    const helperClasses = useHelperStyles()
    const { api } = useApiHttpState()
    const addSnackbarQueue = useAddSnackbarQueue()
    //the shared dialog (context/DialogContext), which is where the cancellation
    //confirmation is rendered - see CancelConfirmation.js
    const openDialog = useOpenDialog()
    const closeDialog = useCloseDialog()

    //`appointment` supplied by the caller wins, so an already loaded row is not fetched twice
    const {
        appointment: fetchedAppointment,
        isLoading: appointmentLoading,
        isError: appointmentError,
        mutate: mutateAppointment,
    } = useAppointment(appointmentProp ? null : appointmentId)
    const source = appointmentProp || fetchedAppointment
    const isEdit = !!(appointmentProp || appointmentId)

    // Requirement 13.5 - a cancelled appointment is READ ONLY. Anything that is
    // not 'scheduled' is treated as frozen rather than testing for 'cancelled'
    // alone, so a status added to the enum later cannot silently become editable.
    // The api refuses the update too (400 'Cancelled appointments cannot be
    // modified'), but a form that offers an edit it will then reject is a trap,
    // so the fields are disabled and the Save button is not rendered at all.
    const isCancelled = !!(source && source.status && source.status !== 'scheduled')

    const { households, isError: householdsError } = useHouseholds()
    const { handlers, isError: handlersError, mutate: mutateHandlers } = useAppointmentHandlers()
    const { appointmentTypes, isError: typesError, mutate: mutateTypes } = useAppointmentTypes()
    const { appointmentDurations, isError: durationsError, mutate: mutateDurations } = useAppointmentDurations()
    const appointmentFormikRef = useRef(null)

    // Only a complete lookup row is a selectable type. This keeps malformed
    // responses out of the select and lets a sole usable type be chosen for the
    // rep without treating it as a missing required choice.
    const validAppointmentTypes = useMemo(
        () =>
            appointmentTypes.filter(
                appointmentType =>
                    appointmentType &&
                    Number.isFinite(Number(appointmentType.id)) &&
                    Number(appointmentType.id) > 0 &&
                    typeof appointmentType.name === 'string' &&
                    !!appointmentType.name.trim()
            ),
        [appointmentTypes]
    )

    // The types lookup completes after Formik has mounted. Selecting through its
    // ref avoids reinitializing the form (which could overwrite a create-mode
    // choice) while still setting the one available valid type automatically.
    useEffect(() => {
        const formik = appointmentFormikRef.current
        if (!formik || isEdit || validAppointmentTypes.length !== 1) return
        const appointmentTypeId = formik.values && formik.values.appointmentTypeId
        if (appointmentTypeId !== '' && appointmentTypeId !== null && appointmentTypeId !== undefined) return
        formik.setFieldValue('appointmentTypeId', validAppointmentTypes[0].id, false)
    }, [isEdit, validAppointmentTypes])

    //{status,message,conflict} of the last rejected save - Requirements 1.6 / 1.7
    const [serverError, setServerError] = useState(null)
    //bumped on every rejected save so the sidebar refetches the handler's week
    const [conflictToken, setConflictToken] = useState(0)

    const storedDateOnly = source ? toDateOnly(source.date) : null
    const todayOnly = toDateOnly(new Date())

    // Requirement 1.6 - today is the earliest selectable date. IN EDIT MODE an
    // appointment already in the past must stay openable (its notes are still
    // editable, and the api only re-validates `date` when the body carries it),
    // so the floor drops to the stored date for that record only.
    const earliestDateOnly = isEdit && storedDateOnly && storedDateOnly < todayOnly ? storedDateOnly : todayOnly
    const earliestDate = toLocalDate(earliestDateOnly)

    // The Jewish calendar for the pickable window. Yom Tov is blocked out because
    // the api refuses it too; Chol HaMoed is flagged but stays selectable.
    //
    // Fetched from the earliest pickable day rather than today so an edit whose
    // stored date is in the past still has calendar data for its own day.
    const { holidays } = useJewishHolidays(earliestDateOnly)
    const holidayIndex = useMemo(() => holidaysByDate(holidays), [holidays])

    // MUI hands `shouldDisableDate` a Date; toDateOnly reads its local fields, so
    // no timezone conversion happens (see convention 2 in calendarHelpers).
    const isBlockedHoliday = useMemo(
        () => day => isWorkForbiddenDay(holidayIndex, day),
        [holidayIndex]
    )

    // The picker's own day cells, annotated. `renderDay` gets the default element
    // back with a title added, so the existing selected/disabled styling is kept
    // and a holiday name is discoverable on hover and to assistive tech.
    const renderHolidayDay = useMemo(
        () => (day, selectedDate, isInCurrentMonth, dayComponent) => {
            const label = isInCurrentMonth ? holidayLabel(holidayIndex, day) : ''
            if (!label) return dayComponent
            const blocked = isWorkForbiddenDay(holidayIndex, day)
            const suffix = blocked
                ? ' - appointments cannot be scheduled'
                : isCholHaMoedDay(holidayIndex, day)
                ? ' (Chol HaMoed)'
                : ''
            return React.cloneElement(dayComponent, { title: `${label}${suffix}` })
        },
        [holidayIndex]
    )

    const initialValues = useMemo(() => {
        const dateOnly = source ? storedDateOnly : toDateOnly(dateProp)
        return {
            householdId: source ? source.householdId : householdIdProp || '',
            handlerId: source ? source.handlerId : handlerIdProp || '',
            appointmentTypeId: source ? source.appointmentTypeId : '',
            date: toLocalDate(dateOnly),
            startTime: toTimeDate(source ? source.startTime : startTimeProp, dateOnly),
            durationMinutes: source ? Number(source.durationMinutes) : durationMinutesProp || '',
            notes: source && source.notes ? source.notes : '',
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source, storedDateOnly, householdIdProp, handlerIdProp, dateProp, startTimeProp, durationMinutesProp])

    //the same normalization on both sides of the edit-mode diff below
    const toPayload = values => ({
        householdId: Number(values.householdId),
        handlerId: Number(values.handlerId),
        appointmentTypeId: Number(values.appointmentTypeId),
        //Requirement 1.6 / the api contract: a DATE-ONLY string. Sending a Date or
        //an ISO timestamp would let the day shift by one in any US timezone.
        date: toDateOnly(values.date),
        startTime: toTimeParam(values.startTime),
        durationMinutes: Number(values.durationMinutes),
        notes: values.notes ? String(values.notes).trim() : '',
    })

    const storedPayload = useMemo(() => (source ? toPayload(initialValues) : null), [source, initialValues]) // eslint-disable-line react-hooks/exhaustive-deps

    const householdOptions = useMemo(
        () => [...households].sort((a, b) => householdLabel(a).localeCompare(householdLabel(b))),
        [households]
    )
    //a call center has thousands of households: the list is filtered by what the
    //user types and capped, so the popup never renders the whole table
    const filterHouseholds = useMemo(() => createFilterOptions({ limit: 50, stringify: householdLabel }), [])

    // Requirement 1.2 / 1.3 - durations are DATA (`appointmentDurations` rows are
    // {id, minutes}), never a hard coded list. An appointment saved with a
    // duration that has since been removed from the table still needs its own
    // option, or opening it would silently blank the select.
    const durationOptions = useMemo(() => {
        const options = appointmentDurations
            .map(duration => ({ key: `duration-${duration.id}`, minutes: Number(duration.minutes) }))
            .filter(option => Number.isFinite(option.minutes) && option.minutes > 0)
        const stored = source ? Number(source.durationMinutes) : null
        if (stored && !options.some(option => option.minutes === stored))
            options.push({ key: `duration-stored-${stored}`, minutes: stored })
        return options.sort((a, b) => a.minutes - b.minutes)
    }, [appointmentDurations, source])

    // Requirements 1.4 / 1.5 - every one of the six fields is required, and the
    // message names the field so the user is told WHICH one is missing. The
    // typeError messages matter as much as the required ones: an untouched select
    // holds '', which casts to NaN rather than failing `required`.
    const validationSchema = Yup.object().shape({
        householdId: Yup.number().typeError('Household is required').required('Household is required'),
        handlerId: Yup.number().typeError('Handler is required').required('Handler is required'),
        appointmentTypeId: Yup.number()
            .typeError('Appointment type is required')
            .required('Appointment type is required'),
        date: Yup.date()
            .typeError('Date must be a valid date')
            .required('Date is required')
            //Requirement 1.6, with the edit-mode exception explained at earliestDateOnly
            .test('not-in-the-past', 'Date must be today or a future date', value => {
                const dateOnly = toDateOnly(value)
                if (!dateOnly) return true
                return dateOnly >= earliestDateOnly
            }),
        startTime: Yup.date().typeError('Start time must be a valid time').required('Start time is required'),
        durationMinutes: Yup.number().typeError('Duration is required').required('Duration is required'),
        notes: Yup.string().max(2000, 'Notes cannot exceed 2000 characters'),
    })

    const handleSubmit = async (values, { setSubmitting }) => {
        setServerError(null)

        // Requirement 13.5, belt and braces: the Save button is not rendered for a
        // cancelled appointment and every field is disabled, but a submit can still
        // be raised (Enter in a field, a stale render), and it must not reach the
        // api to be told no.
        if (isCancelled) {
            setSubmitting(false)
            setServerError({ status: null, message: CANCELLED_MESSAGE, conflict: null })
            return
        }

        const payload = toPayload(values)

        // PARTIAL update (the api patches only what the body carries): sending an
        // unchanged field is harmless but sending nothing at all is cheaper, and it
        // keeps the "SMS only when the schedule moved" rule on the server honest
        // (Requirement 6.1 / 6.2 compares stored vs saved, so an unchanged field
        // never counts either way).
        const changed = storedPayload
            ? Object.keys(payload).filter(key => payload[key] !== storedPayload[key])
            : Object.keys(payload)

        if (isEdit && !changed.length) {
            setSubmitting(false)
            addSnackbarQueue('No changes to save')
            if (onSaved) onSaved(source, { mode: 'edit' })
            return
        }

        const body = isEdit ? changed.reduce((data, key) => ({ ...data, [key]: payload[key] }), {}) : payload
        const [er, response] = isEdit
            ? await updateAppointment(api, source.id, body)
            : await createAppointment(api, body)
        setSubmitting(false)

        if (er) {
            const error = readServerError(er, 'The appointment could not be saved. Please try again.')
            setServerError(error)
            //the handler's week is now suspect whichever way the save failed
            setConflictToken(token => token + 1)
            addSnackbarQueue(error.message, 'error')
            return
        }

        const saved = response ? response.data : null
        if (mutateAppointment) mutateAppointment()
        addSnackbarQueue(`Appointment was ${isEdit ? 'updated' : 'scheduled'}`, 'success')
        if (onSaved) onSaved(saved, { mode: isEdit ? 'edit' : 'create' })
    }

    // Requirement 13.2 - reached only from the confirmation dialog, so the api has
    // already said yes by the time we are here. `cancelled` is the updated row; it
    // falls back to the row we had (with the status patched on) so the caller is
    // never handed null.
    const handleCancelled = cancelled => {
        //this record's own key: reopening it must show the cancelled state, not the
        //scheduled copy that is still in the SWR cache
        if (mutateAppointment) mutateAppointment()
        const row = cancelled || (source ? { ...source, status: 'cancelled' } : null)
        if (onSaved) onSaved(row, { mode: 'cancel' })
    }

    // The dialog owns the confirmation AND the request (context/components/Dialog.js
    // traps focus, autofocuses its Close button and closes on Escape or the X), so
    // the destructive action is never one stray click away and dismissing is the
    // easy path.
    const openCancelDialog = () => {
        if (!source || isCancelled) return
        openDialog(
            'Cancel Appointment',
            <CancelConfirmation appointment={source} onCancelled={handleCancelled} close={closeDialog} />,
            { dialogProps: { maxWidth: 'sm' } }
        )
    }

    // Edit mode waits for the record: repopulating the fields underneath somebody
    // who has started typing would be worse than a moment of progress bar.
    if (isEdit && !source)
        return (
            <div className={className}>
                {!!appointmentLoading && <LinearProgress aria-label="Loading appointment" />}
                {!!appointmentError && (
                    <div className={classes.formErrorSummary} role="alert">
                        <Typography variant="body2" color="secondary">
                            This appointment could not be retrieved.
                        </Typography>
                        {/* the retry calls mutate with NO arguments: handing it the click
                            event would write that event into the SWR cache as the
                            appointment */}
                        <Button
                            size="small"
                            color="primary"
                            variant="outlined"
                            onClick={() => mutateAppointment()}
                            className={classes.margin}
                        >
                            Retry
                        </Button>
                    </div>
                )}
            </div>
        )

    //named so the message can say WHICH list is unavailable rather than "an error occurred"
    const failedLookups = [
        householdsError ? 'households' : null,
        handlersError ? 'handlers' : null,
        typesError ? 'appointment types' : null,
        durationsError ? 'durations' : null,
    ].filter(Boolean)

    const retryLookups = () => {
        if (handlersError && mutateHandlers) mutateHandlers()
        if (typesError && mutateTypes) mutateTypes()
        if (durationsError && mutateDurations) mutateDurations()
    }

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Formik
                innerRef={appointmentFormikRef}
                initialValues={initialValues}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({
                    values,
                    errors,
                    touched,
                    setErrors,
                    setFieldValue,
                    setFieldTouched,
                    setTouched,
                    setValues,
                    submitForm,
                    isSubmitting,
                    submitCount,
                    validateForm,
                }) => {
                    // Requirement 13.5 - one flag for "these fields cannot be
                    // touched": either a save is in flight, or the record is
                    // cancelled and frozen for good.
                    const fieldsDisabled = isSubmitting || isCancelled
                    // Names the holiday when the currently held date is one work is
                    // not permitted on. Normally unreachable through the picker,
                    // which blocks those days, but the date can also be TYPED, and
                    // an edit may already hold such a date from before this rule.
                    const blockedHolidayReason = workForbiddenReason(holidayIndex, values.date)
                    const hasSingleAutomaticAppointmentType = !isEdit && validAppointmentTypes.length === 1
                    const householdError = touched.householdId && !!errors.householdId
                    const selectedHousehold =
                        householdOptions.find(household => household.id === Number(values.householdId)) ||
                        (source && source.household && Number(source.household.id) === Number(values.householdId)
                            ? source.household
                            : null)
                    // Requirement 1.5 - after a rejected submit, everything that is
                    // missing or invalid is listed in ONE place. The per-field
                    // helper texts stay too: the summary tells the user what is
                    // wrong, the field tells them where.
                    const missingMessages = REQUIRED_FIELDS.concat('notes')
                        .filter(field => errors[field])
                        .map(field => errors[field])

                    return (
                        <Form className={className} noValidate>
                            {/* Requirements 13.2 / 13.5 - a cancelled appointment says so
                                at the TOP of the form, before the fields it explains, and
                                in words: the greyed out controls are the consequence, not
                                the message. role="status" rather than "alert" because this
                                is the state of the record on open, not a reaction to
                                something the user just did. */}
                            {!!isCancelled && (
                                <div className={classes.formCancelledNotice} role="status">
                                    <Typography variant="body2" color="secondary">
                                        {CANCELLED_MESSAGE}
                                    </Typography>
                                    <Typography variant="caption" color="text" colorBrightness="secondary">
                                        The record is kept for history. To rebook, schedule a new appointment.
                                    </Typography>
                                </div>
                            )}
                            <Grid container spacing={0}>
                                <Grid item xs={12} md={showHandlerCalendar ? 7 : 12} className={classes.formSection}>
                                    <Grid container spacing={0}>
                                        <Grid item xs={12} className={helperClasses.itemPadding}>
                                            {/* Requirement 1.1 - searchable household select. Formik holds the
                                                ID (that is what the api wants); the Autocomplete is given the
                                                matching OPTION OBJECT so it can render and filter on the name.
                                                Typing filters, arrows + Enter select, Escape closes - all from
                                                Autocomplete itself. */}
                                            <Autocomplete
                                                id="appointment-household"
                                                options={householdOptions}
                                                filterOptions={filterHouseholds}
                                                getOptionLabel={householdLabel}
                                                getOptionSelected={(option, value) =>
                                                    !!option && !!value && option.id === value.id
                                                }
                                                renderOption={option => householdLabel(option)}
                                                value={selectedHousehold}
                                                disabled={fieldsDisabled || lockHousehold}
                                                onChange={(event, option) => {
                                                    setFieldValue('householdId', option ? option.id : '')
                                                    setFieldTouched('householdId', true, false)
                                                }}
                                                onBlur={() => setFieldTouched('householdId', true)}
                                                renderInput={params => (
                                                    <MuiTextField
                                                        {...params}
                                                        label="Household"
                                                        required
                                                        variant="outlined"
                                                        margin="normal"
                                                        size="small"
                                                        fullWidth
                                                        error={householdError}
                                                        //MUI links this to the input via aria-describedby
                                                        //because the field carries an id
                                                        helperText={householdError ? errors.householdId : ' '}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} className={helperClasses.itemPadding}>
                                            {/* Requirement 3.2 - only active users holding the Appointment
                                                Handler role come back from the api, so no filtering here */}
                                            <Field
                                                component={TextField}
                                                select={true}
                                                id="appointment-handler"
                                                name="handlerId"
                                                label="Handler"
                                                required
                                                disabled={fieldsDisabled}
                                                variant="outlined"
                                                margin="normal"
                                                size="small"
                                                fullWidth
                                            >
                                                {handlers.map(handler => (
                                                    <MenuItem key={handler.id} value={handler.id}>
                                                        {formatHandlerName(handler)}
                                                    </MenuItem>
                                                ))}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6} className={helperClasses.itemPadding}>
                                            <Field
                                                component={TextField}
                                                select={true}
                                                id="appointment-type"
                                                name="appointmentTypeId"
                                                label="Appointment Type"
                                                required={!hasSingleAutomaticAppointmentType}
                                                disabled={fieldsDisabled}
                                                variant="outlined"
                                                margin="normal"
                                                size="small"
                                                fullWidth
                                            >
                                                {validAppointmentTypes.map(appointmentType => (
                                                    <MenuItem key={appointmentType.id} value={appointmentType.id}>
                                                        {appointmentType.name}
                                                    </MenuItem>
                                                ))}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={4} className={helperClasses.itemPadding}>
                                            <Field
                                                component={TextField}
                                                select={true}
                                                id="appointment-duration"
                                                name="durationMinutes"
                                                label="Duration"
                                                required
                                                disabled={fieldsDisabled}
                                                variant="outlined"
                                                margin="normal"
                                                size="small"
                                                fullWidth
                                            >
                                                {durationOptions.map(option => (
                                                    <MenuItem key={option.key} value={option.minutes}>
                                                        {formatDuration(option.minutes)}
                                                    </MenuItem>
                                                ))}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={4} className={helperClasses.itemPadding}>
                                            {/* Requirement 1.6 - `minDate` DISABLES past days in the picker.
                                                The api re-checks it anyway, and a rejection from there lands in
                                                the summary below. Keyboard variant so the date can be typed.

                                                `shouldDisableDate` additionally blocks out the Jewish holidays
                                                work is not permitted on, which the api also refuses (400). While
                                                the holiday list is still loading nothing is blocked - the same
                                                fail-open stance the api takes - so a slow request can never
                                                silently prevent a legitimate booking. */}
                                            <Field
                                                component={KeyboardDatePicker}
                                                id="appointment-date"
                                                name="date"
                                                label="Date"
                                                required
                                                disabled={fieldsDisabled}
                                                format="MM/dd/yyyy"
                                                mask="__/__/____"
                                                minDate={earliestDate}
                                                minDateMessage="Date must be today or a future date"
                                                shouldDisableDate={isBlockedHoliday}
                                                renderDay={renderHolidayDay}
                                                inputVariant="outlined"
                                                margin="normal"
                                                size="small"
                                                fullWidth
                                            />
                                            {/* Named in text, never colour alone: the picker greys the day out,
                                                and this says which holiday it was and why. */}
                                            {blockedHolidayReason ? (
                                                <Typography variant="caption" color="error" component="p">
                                                    {blockedHolidayReason}
                                                </Typography>
                                            ) : null}
                                        </Grid>
                                        <Grid item xs={12} sm={4} className={helperClasses.itemPadding}>
                                            <Field
                                                component={KeyboardTimePicker}
                                                id="appointment-start-time"
                                                name="startTime"
                                                label="Start Time"
                                                required
                                                disabled={fieldsDisabled}
                                                format="hh:mm a"
                                                mask="__:__ _M"
                                                inputVariant="outlined"
                                                margin="normal"
                                                size="small"
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={12} className={helperClasses.itemPadding}>
                                            <Field
                                                component={TextField}
                                                id="appointment-notes"
                                                name="notes"
                                                label="Notes"
                                                disabled={fieldsDisabled}
                                                multiline
                                                rows={3}
                                                variant="outlined"
                                                margin="normal"
                                                size="small"
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={12} className={helperClasses.itemPadding}>
                                            <div className={classes.formActions}>
                                                {/* the caller's own slot, kept leftmost */}
                                                {extraActions}

                                                {/* Requirement 13.2 - edit mode only, and only while the
                                                    appointment is still scheduled: cancelling an already
                                                    cancelled record is a no-op server side, so offering the
                                                    button would be offering nothing. Labelled with the noun
                                                    and coloured as destructive so it cannot be mistaken for
                                                    "Close" (which cancels nothing but the panel). */}
                                                {!!isEdit && !isCancelled && (
                                                    <Button
                                                        variant="contained"
                                                        color="error"
                                                        onClick={openCancelDialog}
                                                        disabled={isSubmitting}
                                                        className={helperClasses.smMargin}
                                                    >
                                                        Cancel Appointment
                                                    </Button>
                                                )}

                                                {!!onClose && (
                                                    <Button
                                                        onClick={onClose}
                                                        className={helperClasses.smMargin}
                                                        disabled={isSubmitting}
                                                    >
                                                        Close
                                                    </Button>
                                                )}

                                                {/* Requirement 13.5 - NOT RENDERED for a cancelled
                                                    appointment: a permanently dead Save button is worse
                                                    than none, and the banner above already says why there
                                                    is nothing to save */}
                                                {!isCancelled && (
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={submitForm}
                                                        pending={isSubmitting}
                                                        className={helperClasses.smMargin}
                                                    >
                                                        {isEdit ? 'Save Changes' : 'Schedule Appointment'}
                                                    </Button>
                                                )}
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {/* Requirements 9.1 / 9.3 - the selected handler's week. For a
                                    new or scheduled appointment, available 30-minute slots set the
                                    Date and Start Time; appointment blocks remain non-selectable. The
                                    calendar owns its client-side availability display while the api
                                    still validates a stale or concurrent save. */}
                                {!!showHandlerCalendar && (
                                    <Grid item xs={12} md={5} className={classes.formSidebar}>
                                        <HandlerWeekCalendar
                                            handlerId={values.handlerId}
                                            selectedDate={toDateOnly(values.date)}
                                            selectedStartTime={toTimeParam(values.startTime)}
                                            durationMinutes={values.durationMinutes}
                                            minimumDate={earliestDateOnly}
                                            reloadToken={conflictToken}
                                            excludedAppointmentId={isEdit ? source.id : null}
                                            slotSelectionDisabled={fieldsDisabled}
                                            onSlotSelect={
                                                !isCancelled
                                                    ? slot => {
                                                          const selectedDate = toLocalDate(slot.date)
                                                          const selectedStartTime = toTimeDate(slot.startMinutes, selectedDate)
                                                          if (!selectedDate || !selectedStartTime) return

                                                          // Formik validates every setFieldValue call. Updating date
                                                          // and time separately therefore let its first validation run
                                                          // against an intermediate state on the initial slot click.
                                                          // Commit the pair together, clear only their stale messages,
                                                          // then validate the complete next state.
                                                          const nextValues = {
                                                              ...values,
                                                              date: selectedDate,
                                                              startTime: selectedStartTime,
                                                          }
                                                          const nextTouched = { ...touched, date: true, startTime: true }
                                                          const nextErrors = { ...errors }
                                                          delete nextErrors.date
                                                          delete nextErrors.startTime
                                                          setValues(nextValues, false)
                                                          setTouched(nextTouched, false)
                                                          setErrors(nextErrors)
                                                          validateForm(nextValues)
                                                      }
                                                    : undefined
                                            }
                                        />
                                    </Grid>
                                )}
                            </Grid>

                            {/* Requirement 1.5 - what is missing, in words, in one place */}
                            {!!submitCount && !!missingMessages.length && (
                                <div className={classes.formErrorSummary} role="alert">
                                    <Typography variant="body2" color="secondary">
                                        This appointment cannot be saved yet:
                                    </Typography>
                                    <ul className={classes.formErrorList}>
                                        {missingMessages.map(message => (
                                            <li key={message}>
                                                <Typography variant="body2" color="secondary">
                                                    {message}
                                                </Typography>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Requirements 1.6 / 1.7 - whatever the api rejected it with, including
                                the conflicting slot from a 409 */}
                            {!!serverError && (
                                <div className={classes.formErrorSummary} role="alert">
                                    <Typography variant="body2" color="secondary">
                                        {serverError.message}
                                    </Typography>
                                    {!!describeConflict(serverError.conflict) && (
                                        <Typography variant="body2" color="secondary">
                                            {describeConflict(serverError.conflict)}
                                        </Typography>
                                    )}
                                    {serverError.status === 409 && (
                                        <Typography variant="caption" color="text" colorBrightness="secondary">
                                            Pick another time - the handler's week beside the form has been
                                            refreshed.
                                        </Typography>
                                    )}
                                </div>
                            )}

                            {/* the selects can only offer what loaded, so a failed lookup is said out
                                loud rather than looking like an empty list */}
                            {!!failedLookups.length && (
                                <div className={classes.formErrorSummary} role="alert">
                                    <Typography variant="body2" color="secondary">
                                        {`These options could not be loaded: ${failedLookups.join(', ')}.`}
                                    </Typography>
                                    <Button
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        onClick={retryLookups}
                                        className={classes.margin}
                                    >
                                        Retry
                                    </Button>
                                </div>
                            )}

                            {!!isSubmitting && <LinearProgress />}
                        </Form>
                    )
                }}
            </Formik>
        </MuiPickersUtilsProvider>
    )
}

export default AppointmentForm
